import { C8Node, cnl } from "csi/typings";
import { getFileNameExtension } from "csi/utility/fileUtility";
import { IModelFileType } from "@centricsoftware/model-viewer";
import reflection from "csi/reflection";

type IStyleModels = "gltf" | "glb" | "obj" | "fbx" | "3dm" | "stl";

export const THREED_VIEWABLE = "3D Viewable";
export const DOCUMENT = "Document";
export const DOCUMENT_REVISION = "DocumentRevision";
export const U3M = "Unified 3D Material";
export const THREE_D_MATERIAL_SUBTYPE_URLS = [reflection.URL_SUBTYPE_UNIFIED_3D_MATERIAL, reflection.URL_SUBTYPE_CLO, reflection.URL_SUBTYPE_ADOBE_SUBSTANCE];
export const TYPE_MATERIALDRAPE = "MaterialDrape";
export const TYPE_TRIM = "trim";
export const TYPE_ARTWORK = "artwork";
export const TYPE_U3M = "u3mType";
export const TYPE_3DMODEL = "3DModel";
export const ALL_VALID_FILE_TYPES = new Set<IModelFileType>(["glb", "gltf", "stl", "obj", "fbx", "3dm", "u3m"]);
export const MODEL_VALID_FILE_TYPES = new Set<IStyleModels>(["glb", "gltf", "stl", "obj", "fbx", "3dm"]);
const CENTRIC_DRAPE_FILE = "CentricDrapeFile";

/** Determine if the revision node has an encrypted avatar */
export function isEncrypted(revisionNode: C8Node): boolean {
	const fileChunks = revisionNode.FileChunks;
	const isChunked = !!fileChunks?.trim();

	return isChunked;
}

/** returns if 3d model component is allowed*/
export function is3DModelComponentsAllowed(type: string, subType: string): boolean {
	return type == DOCUMENT && (subType == null || subType == THREED_VIEWABLE);
}

/** returns value from threed configuration */
export function getFromThreeDConfig(val: string, node: C8Node): string {
	const threeDConfig = node?.ThreeDConfiguration;
	return threeDConfig ? JSON.parse(threeDConfig)?.[val] : null;
}

/** returns value from DGaaSGeneratedDocumentType enum */
export function getGeneratedDocumentType(documentRevision: C8Node): string {
	let enumname = "";
	const filename = documentRevision["File.File"];
	const fileExtension = getFileNameExtension(filename);
	switch (fileExtension) {
		case "docx":
			enumname = "DGaaSGeneratedDocumentType:Word";
			break;
		case "pdf":
			enumname = "DGaaSGeneratedDocumentType:PDF";
			break;
		case "xlsx":
			enumname = "DGaaSGeneratedDocumentType:Excel";
			break;
	}
	return enumname;
}

/** searches for an mtl file in a list of alternative files */
export function getMtlFileUrl(documentRevision: C8Node): string | null {
	const alternateFiles = documentRevision.AlternateFiles;
	if (!alternateFiles) {
		return null;
	}
	const mtlFile = Object.keys(alternateFiles)
		.find((key) => getFileNameExtension(key) === "mtl");
	return mtlFile ? alternateFiles[mtlFile] : null;
}

/** determines if the resource URL in the loader is valid- the url is valid if it includes the file value url (as opposed to its filename) */
export function isLoaderResourceURLValid(url: string, documentRevision: C8Node): boolean {
	return Object.values(documentRevision.AlternateFiles || {})
		.some((val:string) => url.includes(val));
}

/** retruns the model file from the alernateFiles list - or return the material drape file */
export function getModelFile(revisionNode: C8Node, modelFile: string): string | null {
	return revisionNode.$Type === TYPE_MATERIALDRAPE ? revisionNode.DrapeFile : revisionNode.AlternateFiles[modelFile];
}

/** returns the model file from the alternate files list */
export function getModelFileUrl(documentRevision: C8Node): string[] | null {
	if (documentRevision.$Type === TYPE_MATERIALDRAPE) {
		return [documentRevision.DrapeFile];
	}
	const alternateFiles = documentRevision.AlternateFiles;
	const modelFiles = Object.keys(alternateFiles)
		.filter((key) => {
			const fileType = getFileNameExtension(key) as IModelFileType;
			return ALL_VALID_FILE_TYPES.has(fileType);
		})
		.filter((key) => !key.includes(CENTRIC_DRAPE_FILE));

	// check if the modelFiles contains a u3m file and if so, return all model files. If not, return the first model file. Ideally we should not allow users to upload multiple model files for a single document revision
	const u3mType = getFromThreeDConfig(TYPE_U3M, documentRevision);
	if (u3mType === TYPE_TRIM) {
		return modelFiles;
	} else {
		return [modelFiles[0]];
	}
}